import React, { useRef } from 'react';
import { observer } from 'mobx-react-lite';
import { Typography, Box, Paper } from '@mui/material';

import RecommendationIconGroup from '../general/RecommendationIconGroup';
import DragHandle from '../general/DragHandle';
import './ProjectHeader.sass';

const ProjectHeader = ({
  noHeaderMap,
  projectDetailStore,
  variant,
  onChangeProjectName,
  onChangeBuildingName,
  onClick,
  project,
  buildings,
  vendor,
  building,
  headerInfo
}) => {
  const mapContainer = useRef(null);
  const buildingNameInput = useRef(null);

  const getOverridenHeaderInfo = () => {
    if (headerInfo) return headerInfo;

    const projectHeaderInfo = projectDetailStore?.headerInfo;
    if (projectHeaderInfo && variant === 'small') {
      const id = projectHeaderInfo.properties?.id;
      const osmId = id
        ? `${id}`.replace('pb_', '').replace('osm-', '').toUpperCase()
        : null;
      const foundBuilding = buildings.find(
        b =>
          b.id === projectHeaderInfo.id?.replace('pb_', '') ||
          b.original_id === osmId
      );
      if (foundBuilding) {
        return {
          ...projectHeaderInfo,
          name: foundBuilding.address || 'Unnamed feature',
          recommendations: foundBuilding?.recommendations || null
        };
      }
      const feature = projectHeaderInfo;
      return {
        ...feature,
        name:
          feature.properties?.name ||
          feature.properties?.Name ||
          'Unnamed feature'
      };
    }
  };

  const overridenHeaderInfo = getOverridenHeaderInfo();

  const BuildingNameInput = building && (
    <input
      onClick={e => e.stopPropagation()}
      ref={buildingNameInput}
      key={building.id}
      type="text"
      defaultValue={building.building_name || building.address}
      onBlur={onChangeBuildingName}
    />
  );

  return (
    <header className="detail-header">
      {!noHeaderMap && (
        <div
          role="region"
          ref={mapContainer}
          onClick={onClick}
          className="detail-header-map"
        />
      )}
      <Paper className="project-header-info" elevation={0} square>
        {variant === 'small' && (
          <>
            <DragHandle />
            <div
              role="region"
              onClick={onClick}
              className="project-header-info-click-overlay"
            />
          </>
        )}
        {overridenHeaderInfo && (
          <OverridenHeaderInfo onClick={onClick} info={overridenHeaderInfo} />
        )}
        <ProjectDetails
          {...{
            project,
            building,
            buildings,
            vendor,
            onChangeProjectName,
            BuildingNameInput,
            headerInfo
          }}
        />
      </Paper>
    </header>
  );
};

const OverridenHeaderInfo = ({ onClick, info }) => (
  <div role="region" onClick={onClick} className="project-header-hover-overlay">
    <Box p={2}>
      <Typography variant="h6">{info.name}</Typography>
      <Box display="flex">
        {info.recommendations && (
          <RecommendationIconGroup {...info.recommendations} />
        )}
        <Typography style={{ opacity: 0.8 }} variant="body2">
          {info.description || info.properties?.description}
        </Typography>
      </Box>
    </Box>
  </div>
);

const ProjectDetails = observer(
  ({
    project,
    building,
    buildings,
    vendor,
    onChangeProjectName,
    BuildingNameInput,
    headerInfo
  }) => (
    <Box p={3} pt={2} pb={2} style={{ zIndex: 5 }}>
      {project && (!building || buildings.length === 1) && (
        <Typography gutterBottom variant="h6">
          <input
            onClick={e => e.stopPropagation()}
            disabled={vendor}
            onChange={onChangeProjectName}
            type="text"
            value={vendor ? `${project.name} - ${vendor.name}` : project.name}
            onBlur={onChangeProjectName}
          />
        </Typography>
      )}
      {project && building && buildings.length !== 1 && (
        <Typography gutterBottom variant="h6">
          {BuildingNameInput}
        </Typography>
      )}
      {!project && building && (
        <Typography gutterBottom variant="h6">
          {building.address}
        </Typography>
      )}
      {!headerInfo && (
        <ProjectDescription
          {...{ project, building, buildings, BuildingNameInput }}
        />
      )}
    </Box>
  )
);

const ProjectDescription = observer(
  ({ project, building, buildings, BuildingNameInput }) => (
    <Box display="flex">
      {project && <RecommendationIconGroup {...project.recommendations} />}
      <Typography style={{ opacity: 0.8 }} variant="body2">
        {project && !building && buildings.length !== 1 && (
          <>{buildings.length} buildings</>
        )}
        {project && building && buildings.length !== 1 && <>{project.name}</>}
        {project && building && buildings.length === 1 && (
          <>{BuildingNameInput}</>
        )}
      </Typography>
    </Box>
  )
);

export default observer(ProjectHeader);
